/**
 * Geometría geodésica. Funciones puras sobre pares [lat, lng] en grados.
 *
 * Se usa la esfera de radio ecuatorial WGS84 con aproximación de Mercator local:
 * suficiente para recintos de unos pocos cientos de metros, que es la escala de
 * trabajo de una lámina de cobertura.
 */

export const EARTH_RADIUS_M = 6378137;

const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

/** Distancia geodésica en metros entre dos coordenadas (haversine). */
export function distanceMeters(lat1, lng1, lat2, lng2) {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
}

/**
 * Punto a `meters` de distancia desde (lat, lng) siguiendo un rumbo.
 * El rumbo se mide en grados con 0 = norte y sentido horario.
 */
export function destinationPoint(lat, lng, meters, bearingDeg) {
  const latRad = toRad(lat);
  const bearing = toRad(bearingDeg);
  const dLat = toDeg((meters * Math.cos(bearing)) / EARTH_RADIUS_M);
  const dLng = toDeg((meters * Math.sin(bearing)) / (EARTH_RADIUS_M * Math.cos(latRad)));
  return [lat + dLat, lng + dLng];
}

/** Rumbo en grados (0 = norte, horario) desde un centro hacia un punto. */
export function bearingBetween(centerLat, centerLng, pointLat, pointLng) {
  const dx = (pointLng - centerLng) * Math.cos(toRad(centerLat));
  const dy = pointLat - centerLat;
  return (toDeg(Math.atan2(dx, dy)) + 360) % 360;
}

/**
 * Superficie en m² de un polígono definido por vértices [lat, lng].
 * Fórmula del exceso esférico; el anillo se cierra solo.
 * Menos de 3 vértices no encierra área.
 */
export function polygonAreaM2(points) {
  if (!points || points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const [lat1, lng1] = points[i];
    const [lat2, lng2] = points[(i + 1) % points.length];
    sum += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs((sum * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

/** Ray casting: ¿el punto cae dentro del polígono? El borde exacto no está definido. */
export function isPointInPolygon(lat, lng, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [latI, lngI] = polygon[i];
    const [latJ, lngJ] = polygon[j];
    const crosses = latI > lat !== latJ > lat;
    if (crosses && lng < ((lngJ - lngI) * (lat - latI)) / (latJ - latI) + lngI) {
      inside = !inside;
    }
  }
  return inside;
}

/** Caja contenedora [minLat, minLng, maxLat, maxLng] de una lista de puntos. */
export function boundingBox(points) {
  let minLat = 90;
  let maxLat = -90;
  let minLng = 180;
  let maxLng = -180;
  points.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });
  return { minLat, minLng, maxLat, maxLng };
}
